/**
 * AccountPairCard — level 1 of the hedge hierarchy.
 *
 * Renders ONE two-account hedge ("Maven Markets #818434 ↔ Blueberry Funded
 * #XXXX") with firm logos, connection state, and a summary of how many
 * symbol hedges live underneath it. Clicking the card navigates to
 * /pairs/:slug for the level-2 symbol list.
 */

import { motion, useReducedMotion } from "motion/react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FirmLogo } from "@/components/firm-logo";
import type { AccountPairListItem } from "@/lib/api/account-pairs";
import type { ReactNode } from "react";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";
import { useDashboardStore } from "@/store/dashboard.store";
import { LiveDot } from "@/components/live-cell";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

const statusVariants: Record<
  AccountPairListItem["status"],
  "active" | "warning" | "breached"
> = {
  active: "active",
  paused: "warning",
  closed: "breached",
};

function formatLogin(login: string | null): string {
  if (!login) return "—";
  return `#${login}`;
}

/**
 * Both chips have `flex-1` so they always claim equal width and the
 * arrow icon between them sits at the geometric center of the row.
 * However, with content left-aligned inside both chips, a long firm
 * name on one side and a short one on the other made the icon LOOK
 * off-center because the visual mass was lopsided. The `align` prop
 * flips the LEFT chip to right-align its content (logo on the right
 * edge, name pushed toward the divider) so the visual mass is
 * symmetric around the icon regardless of name lengths. The RIGHT
 * chip keeps the default left-aligned layout. Net effect: the
 * arrow stays optically centered between the two firms.
 */
function AccountChip({
  account,
  align = "start",
}: {
  account: AccountPairListItem["longAccount"];
  align?: "start" | "end";
}): ReactNode {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center gap-3",
        align === "end" && "flex-row-reverse",
      )}
    >
      <FirmLogo
        src={account.firmLogo}
        name={account.firmName}
        className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain p-1"
      />
      <div
        className={cn(
          "flex min-w-0 flex-col",
          align === "end" && "items-end",
        )}
      >
        <span
          className={cn(
            "truncate font-sans text-sm font-medium text-foreground",
            align === "end" && "w-full text-right",
          )}
        >
          {account.firmName}
        </span>
        <span
          className={cn(
            "truncate font-sans text-xs text-muted-foreground",
            align === "end" && "w-full text-right",
          )}
        >
          {formatLogin(account.login)} &middot; {account.phase}
        </span>
      </div>
    </div>
  );
}

export function AccountPairCard({
  pair,
  index,
}: {
  pair: AccountPairListItem;
  index: number;
}): ReactNode {
  const reduced = useReducedMotion();
  // One WS per visible card. firstSymbolPairId is null when the pair
  // was just created and hasn't spawned any symbol-pair rows yet — in
  // that case the hook short-circuits (useHedgeSocket(null) is a no-op).
  const firstPairId = pair.firstSymbolPairId ?? null;
  useHedgeSocket(firstPairId);
  const livePair = useDashboardStore((s) =>
    firstPairId ? s.pairs[firstPairId] : undefined,
  );
  const longAcc = livePair?.accounts?.[pair.longAccount.accountId];
  const shortAcc = livePair?.accounts?.[pair.shortAccount.accountId];
  // Hedging: each leg's PnL stands alone — the whole point of the hedge
  // is that one side wins while the other loses. Summing them is near
  // zero by design and hides which side is actually progressing.
  // The account-pair card shows ACCOUNT-WIDE P&L (this account across
  // all its symbol pairs), so accountAggregateOpenPnl is the right
  // value here. Per-symbol leg P&L would be wrong for an account-pair
  // overview that includes multiple symbol hedges.
  const longLivePnl = longAcc?.accountAggregateOpenPnl ?? null;
  const shortLivePnl = shortAcc?.accountAggregateOpenPnl ?? null;
  const connectionStatus: "connecting" | "open" | "closed" =
    livePair?.connectionStatus ?? (firstPairId ? "connecting" : "closed");

  return (
    <Link
      to={`/pairs/${encodeURIComponent(pair.slug)}`}
      className="block h-full"
    >
      <motion.div
        initial={reduced ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={
          reduced
            ? { duration: 0 }
            : { delay: index * 0.03, ease: [0.22, 1, 0.36, 1] }
        }
        className="h-full"
      >
        <Card className="group h-full transition-shadow hover:shadow-lg hover:shadow-accent/5">
          <CardContent className="flex h-full flex-col gap-4 p-4 sm:p-5">
            {/* Header: mode + status + live dot */}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="font-sans text-[11px] capitalize">
                {pair.firmPairMode} Firm Hedge
              </Badge>
              <div className="flex items-center gap-2">
                {firstPairId && <LiveDot status={connectionStatus} showLabel={false} />}
                <Badge
                  variant={statusVariants[pair.status]}
                  className="font-sans text-[11px] capitalize"
                >
                  {pair.status}
                </Badge>
              </div>
            </div>

            {/* Both legs — stack vertically on phones (horizontal axis is
                tight after the 40px logos + arrow circle eat 120px of
                fixed width), revert to side-by-side at sm+. The arrow
                glyph rotates 90° in the stacked layout so it still
                reads as "long ↔ short". `min-w-0` lets each chip's
                truncate-aware children shrink instead of pushing the
                card past the viewport. */}
            <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3">
              <AccountChip account={pair.longAccount} align="end" />
              <div
                className="flex h-8 w-8 shrink-0 self-center items-center justify-center rounded-full border border-border bg-muted/30 sm:h-10 sm:w-10"
              >
                <span
                  className="ci-Arrow_Left_Right rotate-90 text-muted-foreground sm:rotate-0"
                  aria-hidden
                />
              </div>
              <AccountChip account={pair.shortAccount} align="start" />
            </div>

            {/* Footer: symbol count + per-account live PnL. Each leg's
                P&L is keyed by the firm name (the accounts are just two
                slots — direction is decided per-trade). Net sum is
                intentionally absent — it's always ~0 by hedge design. */}
            <div className="mt-auto grid grid-cols-[auto_1fr_1fr] items-end gap-x-2 gap-y-1 border-t border-border pt-3 sm:gap-x-3">
              <div className="flex min-w-0 flex-col">
                <span className="font-sans text-[10px] uppercase tracking-wide text-muted-foreground sm:text-[11px]">
                  Symbols
                </span>
                <span className="font-sans text-sm font-medium text-foreground">
                  {pair.activeSymbolPairs}
                </span>
              </div>
              <div className="flex min-w-0 flex-col items-end">
                <span
                  className="truncate font-sans text-[10px] uppercase tracking-wide text-muted-foreground sm:text-[11px]"
                  title={pair.longAccount.firmName}
                >
                  {pair.longAccount.firmName}
                </span>
                <span className="w-full truncate text-right font-mono text-xs font-semibold tabular-nums text-foreground sm:text-sm">
                  {longLivePnl == null ? "—" : fmtMoney(longLivePnl)}
                </span>
              </div>
              <div className="flex min-w-0 flex-col items-end">
                <span
                  className="truncate font-sans text-[10px] uppercase tracking-wide text-muted-foreground sm:text-[11px]"
                  title={pair.shortAccount.firmName}
                >
                  {pair.shortAccount.firmName}
                </span>
                <span className="w-full truncate text-right font-mono text-xs font-semibold tabular-nums text-foreground sm:text-sm">
                  {shortLivePnl == null ? "—" : fmtMoney(shortLivePnl)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </Link>
  );
}
